import { create } from 'zustand';

interface AuthUser {
  id: string;
  email: string;
  name: string;
  avatarUrl?: string | null;
}

interface OriginalSession {
  user: AuthUser;
  permissions: string[];
  token: string;
  licensingAuthorities: string[];
}

export type { AuthUser };

// True only for users scoped to Sharjah Municipality and not Kalba — mixed-authority
// or unrestricted users fall back to the default Kalba experience.
export function isSharjahOnlyAuthority(licensingAuthorities: string[]): boolean {
  return (
    licensingAuthorities.includes('SHARJAH_MUNICIPALITY') &&
    !licensingAuthorities.includes('KALBA_MUNICIPALITY')
  );
}

// Where to send a user once their session ends (explicit sign-out, idle timeout,
// or a failed token refresh) — Sharjah-scoped users go back to the SM-branded login.
export function getLoginRedirectPath(licensingAuthorities: string[]): string {
  return isSharjahOnlyAuthority(licensingAuthorities) ? '/sm/login' : '/login';
}

interface AuthState {
  user: AuthUser | null;
  permissions: string[];
  licensingAuthorities: string[];
  token: string | null;
  isLoading: boolean;
  mustChangePassword: boolean;
  mfaChallenge: { challengeToken: string } | null;
  sessionTimedOut: boolean;
  // Captured at the moment a session timeout is detected, before clearSession() wipes
  // licensingAuthorities — so the timeout modal can still redirect to the right login
  // screen even though the reactive isSharjahOnly value has gone stale by the time the
  // user confirms the modal.
  timeoutRedirectPath: string;
  originalSession: OriginalSession | null;
  setSession: (
    user: AuthUser,
    permissions: string[],
    token: string,
    mustChangePassword?: boolean,
    licensingAuthorities?: string[],
  ) => void;
  setMfaChallenge: (challengeToken: string) => void;
  clearMfaChallenge: () => void;
  clearSession: () => void;
  setLoading: (loading: boolean) => void;
  startImpersonation: (
    targetUser: AuthUser,
    permissions: string[],
    token: string,
    licensingAuthorities: string[],
  ) => void;
  exitImpersonation: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  permissions: [],
  licensingAuthorities: [],
  token: null,
  isLoading: false,
  mustChangePassword: false,
  mfaChallenge: null,
  sessionTimedOut: false,
  timeoutRedirectPath: '/login',
  originalSession: null,
  setSession: (user, permissions, token, mustChangePassword = false, licensingAuthorities = []) =>
    set({
      user,
      permissions,
      licensingAuthorities,
      token,
      isLoading: false,
      mustChangePassword,
      mfaChallenge: null,
      sessionTimedOut: false,
    }),
  setMfaChallenge: (challengeToken) => set({ mfaChallenge: { challengeToken }, isLoading: false }),
  clearMfaChallenge: () => set({ mfaChallenge: null }),
  clearSession: () => {
    sessionStorage.removeItem('impersonated-user-id');
    set({
      user: null,
      permissions: [],
      licensingAuthorities: [],
      token: null,
      isLoading: false,
      mustChangePassword: false,
      mfaChallenge: null,
      originalSession: null,
    });
  },
  setLoading: (isLoading) => set({ isLoading }),
  startImpersonation: (targetUser, permissions, token, licensingAuthorities) => {
    const state = get();
    if (!state.user || !state.token) return;
    sessionStorage.setItem('impersonated-user-id', targetUser.id);
    set({
      originalSession: {
        user: state.user,
        permissions: state.permissions,
        token: state.token,
        licensingAuthorities: state.licensingAuthorities,
      },
      user: targetUser,
      permissions,
      licensingAuthorities,
      token,
    });
  },
  exitImpersonation: () => {
    const { originalSession } = get();
    if (!originalSession) return;
    sessionStorage.removeItem('impersonated-user-id');
    set({
      user: originalSession.user,
      permissions: originalSession.permissions,
      token: originalSession.token,
      licensingAuthorities: originalSession.licensingAuthorities,
      originalSession: null,
    });
  },
}));
